import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  activateIntegration,
  createApiClient,
  createIntegration,
  createWebhook,
  disableIntegration,
  disableWebhook,
  fetchApiClients,
  fetchIntegrations,
  fetchPlatformUsageSummary,
  fetchWebhookDeliveries,
  fetchWebhooks,
  revokeApiClient,
  rotateApiClientKey,
  testWebhook,
} from '../lib/openPlatformApi';
import { openPlatformQueryKeys } from '../lib/openPlatformQueryKeys';

export const useOpenPlatformData = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    status: 'ALL',
    integrationType: 'ALL',
    eventType: 'ALL',
    scope: 'ALL',
  });
  const [selectedWebhookId, setSelectedWebhookId] = useState(null);

  const summaryQuery = useQuery({
    queryKey: openPlatformQueryKeys.usageSummary,
    queryFn: fetchPlatformUsageSummary,
    refetchInterval: 20000,
  });

  const clientsQuery = useQuery({
    queryKey: openPlatformQueryKeys.clients,
    queryFn: fetchApiClients,
    refetchInterval: 20000,
  });

  const webhooksQuery = useQuery({
    queryKey: openPlatformQueryKeys.webhooks,
    queryFn: fetchWebhooks,
    refetchInterval: 20000,
  });

  const deliveriesQuery = useQuery({
    queryKey: openPlatformQueryKeys.deliveries(selectedWebhookId || 'all'),
    queryFn: () => fetchWebhookDeliveries(selectedWebhookId),
    refetchInterval: 20000,
  });

  const integrationsQuery = useQuery({
    queryKey: openPlatformQueryKeys.integrations,
    queryFn: fetchIntegrations,
    refetchInterval: 20000,
  });

  const refreshAll = () => {
    summaryQuery.refetch();
    clientsQuery.refetch();
    webhooksQuery.refetch();
    deliveriesQuery.refetch();
    integrationsQuery.refetch();
  };

  const invalidateCore = () => {
    queryClient.invalidateQueries({ queryKey: openPlatformQueryKeys.usageSummary });
    queryClient.invalidateQueries({ queryKey: openPlatformQueryKeys.clients });
    queryClient.invalidateQueries({ queryKey: openPlatformQueryKeys.webhooks });
    queryClient.invalidateQueries({ queryKey: openPlatformQueryKeys.deliveries('all') });
    queryClient.invalidateQueries({ queryKey: openPlatformQueryKeys.integrations });
  };

  const createClientMutation = useMutation({
    mutationFn: createApiClient,
    onSuccess: invalidateCore,
  });

  const rotateClientKeyMutation = useMutation({
    mutationFn: rotateApiClientKey,
    onSuccess: invalidateCore,
  });

  const revokeClientMutation = useMutation({
    mutationFn: revokeApiClient,
    onSuccess: invalidateCore,
  });

  const createWebhookMutation = useMutation({
    mutationFn: createWebhook,
    onSuccess: invalidateCore,
  });

  const testWebhookMutation = useMutation({
    mutationFn: testWebhook,
    onSuccess: invalidateCore,
  });

  const disableWebhookMutation = useMutation({
    mutationFn: disableWebhook,
    onSuccess: invalidateCore,
  });

  const createIntegrationMutation = useMutation({
    mutationFn: createIntegration,
    onSuccess: invalidateCore,
  });

  const activateIntegrationMutation = useMutation({
    mutationFn: activateIntegration,
    onSuccess: invalidateCore,
  });

  const disableIntegrationMutation = useMutation({
    mutationFn: disableIntegration,
    onSuccess: invalidateCore,
  });

  const clients = useMemo(() => {
    const rows = clientsQuery.data?.items || [];
    return rows.filter((row) => {
      if (filters.status !== 'ALL' && String(row?.status || '').toUpperCase() !== filters.status) return false;
      if (filters.scope !== 'ALL' && !(row?.scopes || []).includes(filters.scope)) return false;
      return true;
    });
  }, [clientsQuery.data?.items, filters.status, filters.scope]);

  const webhooks = useMemo(() => {
    const rows = webhooksQuery.data?.items || [];
    return rows.filter((row) => {
      if (filters.status !== 'ALL') {
        const normalized = row?.isActive ? 'ACTIVE' : 'DISABLED';
        if (normalized !== filters.status) return false;
      }
      if (filters.eventType !== 'ALL' && !(row?.eventTypes || []).includes(filters.eventType)) return false;
      return true;
    });
  }, [webhooksQuery.data?.items, filters.status, filters.eventType]);

  const integrations = useMemo(() => {
    const rows = integrationsQuery.data?.items || [];
    return rows.filter((row) => {
      if (filters.status !== 'ALL' && String(row?.status || '').toUpperCase() !== filters.status) return false;
      if (filters.integrationType !== 'ALL' && String(row?.integrationType || '').toUpperCase() !== filters.integrationType) return false;
      return true;
    });
  }, [integrationsQuery.data?.items, filters.status, filters.integrationType]);

  const isLoading = summaryQuery.isLoading || clientsQuery.isLoading || webhooksQuery.isLoading || deliveriesQuery.isLoading || integrationsQuery.isLoading;
  const error = summaryQuery.error || clientsQuery.error || webhooksQuery.error || deliveriesQuery.error || integrationsQuery.error;

  return {
    summary: summaryQuery.data || {},
    clients,
    webhooks,
    deliveries: deliveriesQuery.data?.items || [],
    integrations,
    filters,
    setFilters,
    selectedWebhookId,
    setSelectedWebhookId,
    refreshAll,
    isLoading,
    error,
    actions: {
      createClientMutation,
      rotateClientKeyMutation,
      revokeClientMutation,
      createWebhookMutation,
      testWebhookMutation,
      disableWebhookMutation,
      createIntegrationMutation,
      activateIntegrationMutation,
      disableIntegrationMutation,
    },
  };
};
